import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Menu, X, Phone, CalendarDays, Heart, UserRound, LogOut, MessageCircle } from "lucide-react";
import { STORE, getWishlist, waLink } from "../lib/store";
import { useAuth, login } from "../lib/auth";
import { ThemeToggle } from "./ThemeToggle";
import { trackSearch } from "../lib/analytics";
import { VoiceSearchButton } from "./VoiceSearch";
import { loadAliases, expandQuery } from "../lib/voiceAliases";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "./ui/sheet";

const WORLD_NAV = [
  { slug: "gold-world", label: "Gold World" },
  { slug: "diamond-world", label: "Diamond World" },
  { slug: "bridal-world", label: "Bridal World" },
  { slug: "heritage-jewelry", label: "Heritage Jewelry" },
  { slug: "precious-stones", label: "Precious Stones" },
  { slug: "silver-spiritual", label: "Silver & Spiritual" },
  { slug: "custom-design-studio", label: "Custom Design" },
  { slug: "gold-exchange-services", label: "Gold Exchange" },
];

const WORLD_FULL_NAMES = {
  "gold-world": "Gold World",
  "diamond-world": "Diamond World",
  "bridal-world": "Bridal World",
  "heritage-jewelry": "Heritage Jewelry",
  "precious-stones": "Precious Stones",
  "silver-spiritual": "Silver & Spiritual Collection",
  "custom-design-studio": "Custom Design Studio",
  "gold-exchange-services": "Gold Exchange & Services",
};

export default function Header() {
  const [q, setQ] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [wlCount, setWlCount] = useState(0);
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  useEffect(() => {
    const sync = () => setWlCount(getWishlist().length);
    sync();
    window.addEventListener("rj-wishlist", sync);
    return () => window.removeEventListener("rj-wishlist", sync);
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) {
      trackSearch(q.trim());
      navigate(`/shop?search=${encodeURIComponent(q.trim())}`);
      setSearchOpen(false);
      setMobileOpen(false);
    }
  };

  const voiceSearch = async (text) => {
    const q = expandQuery(text, await loadAliases());
    setQ(q);
    trackSearch(q);
    navigate(`/shop?search=${encodeURIComponent(q)}`);
    setSearchOpen(false);
    setMobileOpen(false);
  };

  return (
    <header className="sticky top-0 z-50">
      {/* Announcement bar */}
      <div className="bg-[#faf7f0] border-b border-[#ece5d5] text-[#5c5240] text-xs tracking-[0.14em] sm:tracking-[0.18em] uppercase text-center py-2 px-4">
        USA-wide Delivery &nbsp;•&nbsp; <span className="hidden sm:inline">{STORE.address} &nbsp;•&nbsp;</span>
        <a href={`tel:${STORE.phone}`} className="hover:text-[#a8842c] transition-colors"> {STORE.phone}</a>
      </div>

      <div className="bg-white/95 backdrop-blur border-b border-[#ece5d5]">
        <div className="max-w-[1440px] mx-auto px-3 sm:px-6">
          <div className="flex items-center justify-between h-20 gap-2 sm:gap-3">
            {/* Mobile menu */}
            <div className="xl:hidden">
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" aria-label="Menu">
                    <Menu size={22} strokeWidth={1.5} />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="bg-white w-[300px] overflow-y-auto">
                  <SheetTitle className="sr-only">Menu</SheetTitle>
                  <div className="mt-8 space-y-5">
                    <form onSubmit={submitSearch} className="flex gap-2">
                      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search jewelry..." className="rounded-none border-[#d8cdb5]" />
                      <VoiceSearchButton onResult={voiceSearch} />
                      <Button type="submit" size="icon" className="rounded-none bg-[#171310] shrink-0"><Search size={16} /></Button>
                    </form>
                    <div data-testid="mobile-help-block">
                      <p className="mobile-nav-head">Need Help?</p>
                      <div className="grid grid-cols-2 gap-2 mt-1">
                        <button
                          onClick={() => { setMobileOpen(false); window.dispatchEvent(new CustomEvent("rj-open-chat")); }}
                          data-testid="mobile-help-chat"
                          className="flex items-center justify-center gap-1.5 h-10 bg-[#171310] text-[#e9d9ac] text-[11px] tracking-[0.1em] uppercase"
                        >
                          Ask Mr. Regal
                        </button>
                        <a
                          href={waLink("Hello Regal Jewels! I have a question.")}
                          target="_blank" rel="noopener noreferrer"
                          data-testid="mobile-help-whatsapp"
                          className="flex items-center justify-center gap-1.5 h-10 bg-[#25D366] text-white text-[11px] tracking-[0.1em] uppercase"
                        >
                          <MessageCircle size={13} /> WhatsApp
                        </a>
                        <a
                          href={`tel:${STORE.phone}`}
                          data-testid="mobile-help-call"
                          className="flex items-center justify-center gap-1.5 h-10 border border-[#d8cdb5] text-[#3a352c] text-[11px] tracking-[0.1em] uppercase"
                        >
                          <Phone size={13} /> Call Store
                        </a>
                        <Link
                          to="/book" onClick={() => setMobileOpen(false)}
                          data-testid="mobile-help-book"
                          className="flex items-center justify-center gap-1.5 h-10 border border-[#d8cdb5] text-[#3a352c] text-[11px] tracking-[0.1em] uppercase"
                        >
                          <CalendarDays size={13} /> Book Visit
                        </Link>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <p className="mobile-nav-head">The Eight Worlds</p>
                      {WORLD_NAV.map((w) => (
                        <Link key={w.slug} to={`/regal-world/${w.slug}`} onClick={() => setMobileOpen(false)} className="mobile-nav-link" data-testid={`mobile-nav-${w.slug}`}>
                          {WORLD_FULL_NAMES[w.slug]}
                        </Link>
                      ))}
                    </div>
                    <div className="space-y-1 pt-2 border-t border-[#e8e0d0]">
                      <Link to="/regal-world?view=vault" onClick={() => setMobileOpen(false)} className="mobile-nav-link">The Regal Vault</Link>
                      <Link to="/shop" onClick={() => setMobileOpen(false)} className="mobile-nav-link">Shop All Jewelry</Link>
                      <Link to="/stories" onClick={() => setMobileOpen(false)} className="mobile-nav-link">Regal Stories</Link>
                      <Link to="/finder" onClick={() => setMobileOpen(false)} className="mobile-nav-link">Help Me Choose</Link>
                    </div>
                    <div className="space-y-1 pt-2 border-t border-[#e8e0d0]">
                      <Link to="/book" onClick={() => setMobileOpen(false)} className="mobile-nav-link">Book an Appointment</Link>
                      <Link to="/education" onClick={() => setMobileOpen(false)} className="mobile-nav-link">Jewelry Education</Link>
                      <Link to="/contact" onClick={() => setMobileOpen(false)} className="mobile-nav-link">Contact & About</Link>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t border-[#e8e0d0]">
                      <p className="mobile-nav-head !mb-0">Appearance</p>
                      <ThemeToggle className="border border-[#d8cdb5]" tid="theme-toggle-mobile-btn" />
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>

            {/* Logo */}
            <Link to="/" className="flex items-center gap-1.5 sm:gap-3 shrink-0">
              <img src={STORE.logo} alt="Regal Jewels" className="h-8 sm:h-12 w-auto" />
              <div className="hidden min-[380px]:block">
                <p className="font-heading text-base sm:text-xl tracking-[0.06em] sm:tracking-[0.14em] text-[#171310] leading-none whitespace-nowrap">REGAL JEWELS</p>
                <p className="hidden sm:block text-[10px] tracking-[0.18em] text-[#a8842c] uppercase mt-1 whitespace-nowrap">22kt Gold - Diamonds - 24kt Gold</p>
              </div>
            </Link>

            {/* Right actions */}
            <div className="flex items-center gap-1 sm:gap-2">
              <ThemeToggle className="!hidden sm:!inline-flex" />
              <Button variant="ghost" size="icon" aria-label="Search" onClick={() => setSearchOpen(!searchOpen)} className="hidden sm:inline-flex">
                {searchOpen ? <X size={20} strokeWidth={1.5} /> : <Search size={20} strokeWidth={1.5} />}
              </Button>
              <Link to="/wishlist" aria-label="Wishlist" className="relative" data-testid="header-wishlist-link">
                <Button variant="ghost" size="icon"><Heart size={20} strokeWidth={1.5} /></Button>
                {wlCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-[#b04a3a] text-white text-[9px] h-4 min-w-4 px-1 rounded-full flex items-center justify-center" data-testid="header-wishlist-count">
                    {wlCount}
                  </span>
                )}
              </Link>
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button aria-label="My account" className="h-9 w-9 rounded-full overflow-hidden border border-[#e0d7c2] hover:border-[#a8842c] transition-colors shrink-0" data-testid="header-account-btn">
                      {user.picture ? (
                        <img src={user.picture} alt={user.name} referrerPolicy="no-referrer" className="w-full h-full object-cover" />
                      ) : (
                        <span className="w-full h-full flex items-center justify-center bg-[#f4eddc] text-[#a8842c] text-sm font-medium">{(user.name || "?")[0]}</span>
                      )}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-white border-[#e8e0d0] rounded-none w-52">
                    <DropdownMenuLabel>
                      <p className="text-sm text-[#171310] truncate">{user.name}</p>
                      <p className="text-xs text-[#9c9482] font-normal truncate">{user.email}</p>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate("/wishlist")} className="cursor-pointer" data-testid="account-menu-wishlist">
                      <Heart size={14} className="mr-2" /> My Wishlist
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={logout} className="cursor-pointer text-[#8c2f1b]" data-testid="account-menu-signout">
                      <LogOut size={14} className="mr-2" /> Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button variant="ghost" size="icon" aria-label="Sign in with Google" onClick={login} data-testid="header-signin-btn">
                  <UserRound size={20} strokeWidth={1.5} />
                </Button>
              )}
              <a href={`tel:${STORE.phone}`} className="hidden md:flex" aria-label="Call us">
                <Button variant="ghost" size="icon"><Phone size={19} strokeWidth={1.5} /></Button>
              </a>
              <Link to="/book">
                <Button className="rounded-none bg-[#171310] hover:bg-[#3a2f1d] text-[#e9d9ac] tracking-[0.12em] text-xs uppercase h-10 px-2.5 sm:px-3 lg:px-5 transition-colors duration-300">
                  <CalendarDays size={15} strokeWidth={1.5} className="lg:mr-2" />
                  <span className="hidden lg:inline">Book Appointment</span>
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Desktop nav row — the eight vault worlds, gold on white, centered */}
        <nav className="hidden xl:block border-t border-[#f0e9da]" aria-label="The eight Regal Worlds">
          <div className="max-w-[1440px] mx-auto px-6 flex items-center justify-center gap-7 2xl:gap-9 h-11">
            {WORLD_NAV.map((w) => (
              <Link key={w.slug} to={`/regal-world/${w.slug}`} className="nav-link nav-link-gold" data-testid={`nav-${w.slug}`}>
                {w.label}
              </Link>
            ))}
          </div>
        </nav>

        {/* Search bar */}
        {searchOpen && (
          <div className="border-t border-[#e8e0d0] bg-white py-3 px-4">
            <form onSubmit={submitSearch} className="max-w-2xl mx-auto flex gap-2">
              <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search chains, rings, bangles..." className="rounded-none border-[#d8cdb5] focus-visible:ring-[#a8842c]" />
              <VoiceSearchButton onResult={voiceSearch} />
              <Button type="submit" className="rounded-none bg-[#171310] hover:bg-[#3a2f1d] text-[#e9d9ac] px-6">Search</Button>
            </form>
          </div>
        )}
      </div>
    </header>
  );
}
